import { Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ShareholdersFilter } from '../../../generated/model/shareholdersFilter';
import { useDefaultValueWatcher } from '../../../hooks/useDefaultValueWatcher';
import { useOnChange } from '../../../hooks/useOnChange';
import ReadableStatementView from '../../Inputs/ReadableStatementView';
import { InputPercentageDropdown } from '../../Inputs/ShareHolderDropdown';
import InputSwitch, { SwitchSize } from '../../Inputs/Switch/Switch';

interface Props {
  root?: boolean;
  onChange: (value: ShareholdersFilter) => void;
  defaultValue?: ShareholdersFilter;
  active: boolean;
}

const SwitchShareholderFilterInput: React.FC<Props> = React.memo(
  ({
    onChange,
    root,
    active,
    defaultValue = {
      includeOverlappingOwnership: false,
      includePrivatelyOwned: true,
      includeUnknownShareholders: false,
      percentage: 25,
    },
  }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [state, setState] = useState<ShareholdersFilter>(defaultValue);

    useDefaultValueWatcher({ defaultValue, state, setState });
    useOnChange(onChange, state, root);

    const options: string[] = [];
    if (state?.includePrivatelyOwned) {
      options.push(t('companies:filterInputs.privatelyOwned'));
    }
    if (state?.includeUnknownShareholders) {
      options.push(t('companies:filterInputs.withUnknownShareholders'));
    }
    if (state?.includeOverlappingOwnership) {
      options.push(t('companies:filterInputs.withOverlappingOwnership'));
    }

    const rs: any[] = [];
    if (state?.percentage) {
      rs.push({
        label: 'Ownership %:',
        value: state.percentage,
      });
    }
    if (options.length) {
      rs.push({ label: 'Included:', value: options.join(', ') });
    }

    return (
      <>
        {active ? (
          <>
            <Grid container direction="row">
              <InputPercentageDropdown
                onChange={(value) => {
                  setState({ ...state, percentage: Number(value.percentage) });
                }}
                defaultValue={['' + defaultValue.percentage]}
                defaultState={{ percentage: '' + state.percentage! }}
              />
            </Grid>
            <Grid container direction="row">
              <Typography className={classes.typographyUppercase}>
                {t('companies:filterInputs.includeCompanies')}:
              </Typography>
              <Grid item className={classes.gridText}>
                <Typography className={classes.typography}>
                  {t('companies:filterInputs.privatelyOwned')}
                </Typography>
              </Grid>
              <Grid item>
                <InputSwitch
                  fieldName=""
                  checked={state.includePrivatelyOwned}
                  onChange={(value) => {
                    setState({ ...state, includePrivatelyOwned: value });
                  }}
                  size={SwitchSize.small}
                />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item className={classes.gridText}>
                <Typography className={classes.typography}>
                  {t('companies:filterInputs.withUnknownShareholders')}
                </Typography>
              </Grid>
              <Grid item>
                <InputSwitch
                  fieldName=""
                  checked={state.includeUnknownShareholders}
                  onChange={(value) => {
                    setState({ ...state, includeUnknownShareholders: value });
                  }}
                  size={SwitchSize.small}
                />
              </Grid>
            </Grid>
            <Grid container direction="row">
              <Grid item className={classes.gridText}>
                <Typography className={classes.typography}>
                  {t('companies:filterInputs.withOverlappingOwnership')}
                </Typography>
              </Grid>
              <Grid item>
                <InputSwitch
                  fieldName=""
                  checked={state.includeOverlappingOwnership}
                  onChange={(value) => {
                    setState({ ...state, includeOverlappingOwnership: value });
                  }}
                  size={SwitchSize.small}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <ReadableStatementView rows={rs} />
        )}
      </>
    );
  },
);

const useStyles = makeStyles((theme: Theme) => ({
  gridText: {
    width: 'calc(100% - 48px)',
    display: 'flex',
    alignItems: 'center',
  },
  typography: {
    fontSize: theme.typography.fontSize - 2,
  },
  typographyUppercase: {
    fontSize: theme.typography.fontSize - 2,
    textTransform: 'uppercase',
  },
}));

export default SwitchShareholderFilterInput;
